import React from "react";
import { Helmet } from "react-helmet-async";

import { BACKEND_HOST, SITE_URL } from "../../../api";
import { getPrecouponPackageLines } from "./promotionGiftMappers";

const DEFAULT_OG_IMAGE_PATH = "/android-chrome-512x512.png";
const PUBLIC_URL_TOKEN = "%PUBLIC_URL%";
const ABSOLUTE_URL_PATTERN = /^https?:\/\//i;

const initialSiteMeta =
  typeof window !== "undefined" ? window.AMZ_ENV?.VITE_INITIAL_SITE_META ?? null : null;
const initialRouteMeta =
  typeof window !== "undefined" ? window.AMZ_ENV?.VITE_INITIAL_ROUTE_META ?? null : null;
const initialPromotionCoupon =
  typeof window !== "undefined" ? window.AMZ_ENV?.VITE_INITIAL_PROMOTION_COUPON ?? null : null;
const initialPromotionVoucher =
  typeof window !== "undefined" ? window.AMZ_ENV?.VITE_INITIAL_PROMOTION_VOUCHER ?? null : null;
const initialPromotionPrecoupon =
  typeof window !== "undefined" ? window.AMZ_ENV?.VITE_INITIAL_PROMOTION_PRECOUPON ?? null : null;

function getSiteOrigin() {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }

  return SITE_URL;
}

function getProtocolPrefix(originOrUrl = "") {
  return originOrUrl.startsWith("http://") ? "http:" : "https:";
}

function normalizeText(value, fallback = "") {
  if (typeof value !== "string") {
    return fallback;
  }

  const normalizedValue = value.replace(/\s+/g, " ").trim();
  return normalizedValue || fallback;
}

function stripHtml(value) {
  return normalizeText(typeof value === "string" ? value.replace(/<[^>]*>/g, " ") : "");
}

function truncateText(value, maxLength = 160) {
  if (!value || value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, maxLength - 1).trimEnd()}…`;
}

function resolveAbsoluteImageUrl(rawUrl) {
  const siteOrigin = getSiteOrigin();

  if (!rawUrl) {
    return null;
  }

  if (ABSOLUTE_URL_PATTERN.test(rawUrl) || rawUrl.startsWith("data:")) {
    return rawUrl;
  }

  if (rawUrl.startsWith("//")) {
    return `${getProtocolPrefix(BACKEND_HOST)}${rawUrl}`;
  }

  if (rawUrl.startsWith(PUBLIC_URL_TOKEN)) {
    return `${siteOrigin}${rawUrl.slice(PUBLIC_URL_TOKEN.length)}`;
  }

  if (rawUrl.startsWith("/")) {
    return rawUrl.startsWith(DEFAULT_OG_IMAGE_PATH) ? `${siteOrigin}${rawUrl}` : `${BACKEND_HOST}${rawUrl}`;
  }

  return `${BACKEND_HOST}/${rawUrl.replace(/^\.?\//, "")}`;
}

function summarizeSiteNames(siteNames) {
  if (!Array.isArray(siteNames) || siteNames.length === 0) {
    return "";
  }

  const normalizedSiteNames = siteNames
    .map((siteName) => normalizeText(siteName))
    .filter(Boolean);

  if (normalizedSiteNames.length === 0) {
    return "";
  }

  if (normalizedSiteNames.length === 1) {
    return normalizedSiteNames[0];
  }

  return `${normalizedSiteNames[0]} 외 ${normalizedSiteNames.length - 1}곳`;
}

function summarizeLines(lines, suffix = "개") {
  if (!Array.isArray(lines) || lines.length === 0) {
    return "";
  }

  const normalizedLines = lines
    .map((line) => normalizeText(line))
    .filter(Boolean);

  if (normalizedLines.length === 0) {
    return "";
  }

  if (normalizedLines.length === 1) {
    return normalizedLines[0];
  }

  return `${normalizedLines[0]} 외 ${normalizedLines.length - 1}${suffix}`;
}

function getMatchingInitialRouteMeta(routeType, sharePinCode) {
  if (!initialRouteMeta || initialRouteMeta.routeType !== routeType) {
    return null;
  }

  return initialRouteMeta.sharePinCode === sharePinCode ? initialRouteMeta : null;
}

export function getInitialPromotionCouponData(sharePinCode) {
  if (!initialPromotionCoupon || initialPromotionCoupon.share_pin_code !== sharePinCode) {
    return null;
  }

  return initialPromotionCoupon;
}

export function getInitialPromotionVoucherData(sharePinCode) {
  if (!initialPromotionVoucher || initialPromotionVoucher.share_pin_code !== sharePinCode) {
    return null;
  }

  return initialPromotionVoucher;
}

export function getInitialPromotionPrecouponData(sharePinCode) {
  if (!initialPromotionPrecoupon || initialPromotionPrecoupon.share_pin_code !== sharePinCode) {
    return null;
  }

  return initialPromotionPrecoupon;
}

export function buildPromotionCouponSeoMeta({ couponItem, sharePinCode }) {
  if (!sharePinCode) {
    return initialSiteMeta;
  }

  if (!couponItem) {
    return getMatchingInitialRouteMeta("promotion-coupon", sharePinCode) || initialSiteMeta;
  }

  const siteOrigin = getSiteOrigin();
  const routeUrl = `${siteOrigin}/promotion/coupon/${encodeURIComponent(sharePinCode)}/`;
  const itemName = normalizeText(couponItem?.coupon_name, "프로모션 쿠폰");
  const senderName = normalizeText(
    couponItem?.display_business_name || couponItem?.business_name || couponItem?.username,
    ""
  );
  const siteSummary = summarizeSiteNames(couponItem?.site_names);
  const title = normalizeText(
    senderName ? `${senderName}님이 보낸 ${itemName}` : `${itemName} 쿠폰 선물`,
    itemName
  );
  const couponDescription = truncateText(stripHtml(couponItem?.descript), 160);
  const fallbackDescription = truncateText(
    normalizeText(
      [
        senderName ? `${senderName}님이 보낸 프로모션 쿠폰입니다.` : `${itemName} 프로모션 쿠폰입니다.`,
        siteSummary ? `${siteSummary}에서 사용할 수 있습니다.` : "",
      ]
        .filter(Boolean)
        .join(" ")
    ),
    160
  );
  const ogImage =
    resolveAbsoluteImageUrl(couponItem?.image) ||
    initialSiteMeta?.siteLogoUrl ||
    `${siteOrigin}${DEFAULT_OG_IMAGE_PATH}`;
  const description = couponDescription || fallbackDescription || initialSiteMeta?.description || itemName;

  return {
    routeType: "promotion-coupon",
    sharePinCode,
    title,
    description,
    ogTitle: title,
    ogDescription: description,
    ogImage,
    ogUrl: routeUrl,
    canonical: routeUrl,
  };
}

export function buildPromotionVoucherSeoMeta({ voucherItem, sharePinCode }) {
  if (!sharePinCode) {
    return initialSiteMeta;
  }

  if (!voucherItem) {
    return getMatchingInitialRouteMeta("promotion-voucher", sharePinCode) || initialSiteMeta;
  }

  const siteOrigin = getSiteOrigin();
  const routeUrl = `${siteOrigin}/promotion/voucher/${encodeURIComponent(sharePinCode)}/`;
  const itemName = normalizeText(voucherItem?.voucher_name, "프로모션 무료쿠폰");
  const senderName = normalizeText(
    voucherItem?.display_business_name || voucherItem?.business_name || voucherItem?.username,
    ""
  );
  const siteName = normalizeText(voucherItem?.site_name, "");
  const title = normalizeText(
    senderName ? `${senderName}님이 보낸 ${itemName}` : `${itemName} 쿠폰 선물`,
    itemName
  );
  const voucherDescription = truncateText(stripHtml(voucherItem?.precautions), 160);
  const fallbackDescription = truncateText(
    normalizeText(
      [
        senderName ? `${senderName}님이 보낸 프로모션 무료쿠폰입니다.` : "프로모션 무료쿠폰입니다.",
        siteName ? `${siteName}에서 사용할 수 있습니다.` : "",
      ]
        .filter(Boolean)
        .join(" ")
    ),
    160
  );
  const ogImage =
    resolveAbsoluteImageUrl(voucherItem?.image) ||
    initialSiteMeta?.siteLogoUrl ||
    `${siteOrigin}${DEFAULT_OG_IMAGE_PATH}`;
  const description = voucherDescription || fallbackDescription || initialSiteMeta?.description || itemName;

  return {
    routeType: "promotion-voucher",
    sharePinCode,
    title,
    description,
    ogTitle: title,
    ogDescription: description,
    ogImage,
    ogUrl: routeUrl,
    canonical: routeUrl,
  };
}

export function buildPromotionPrecouponSeoMeta({ precouponItem, sharePinCode }) {
  if (!sharePinCode) {
    return initialSiteMeta;
  }

  if (!precouponItem) {
    return getMatchingInitialRouteMeta("promotion-precoupon", sharePinCode) || initialSiteMeta;
  }

  const siteOrigin = getSiteOrigin();
  const routeUrl = `${siteOrigin}/promotion/precoupon/${encodeURIComponent(sharePinCode)}/`;
  const itemName = normalizeText(precouponItem?.precoupon_name, "프로모션 예약권");
  const senderName = normalizeText(
    precouponItem?.display_business_name || precouponItem?.business_name || precouponItem?.username,
    ""
  );
  const packageSummary = summarizeLines(getPrecouponPackageLines(precouponItem?.available_packages));
  const siteName = normalizeText(precouponItem?.site_name, "");
  const title = normalizeText(
    senderName ? `${senderName}님이 보낸 ${itemName}` : `${itemName} 예약권 선물`,
    itemName
  );
  const precouponDescription = truncateText(stripHtml(precouponItem?.description), 160);
  const fallbackDescription = truncateText(
    normalizeText(
      [
        senderName ? `${senderName}님이 보낸 예약권입니다.` : `${itemName} 예약권입니다.`,
        packageSummary
          ? `${packageSummary} 예약 패키지에 사용할 수 있습니다.`
          : siteName
            ? `${siteName}에서 사용할 수 있습니다.`
            : "",
      ]
        .filter(Boolean)
        .join(" ")
    ),
    160
  );
  const ogImage =
    resolveAbsoluteImageUrl(precouponItem?.image) ||
    initialSiteMeta?.siteLogoUrl ||
    `${siteOrigin}${DEFAULT_OG_IMAGE_PATH}`;
  const description = precouponDescription || fallbackDescription || initialSiteMeta?.description || itemName;

  return {
    routeType: "promotion-precoupon",
    sharePinCode,
    title,
    description,
    ogTitle: title,
    ogDescription: description,
    ogImage,
    ogUrl: routeUrl,
    canonical: routeUrl,
  };
}

export function PromotionSeoHelmet({ meta }) {
  if (!meta) {
    return null;
  }

  return (
    <Helmet prioritizeSeoTags>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <link rel="canonical" href={meta.canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={meta.ogTitle || meta.title} />
      <meta property="og:description" content={meta.ogDescription || meta.description} />
      <meta property="og:image" content={meta.ogImage} />
      <meta property="og:url" content={meta.ogUrl || meta.canonical} />
    </Helmet>
  );
}
